import { Suspense, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContentCustom,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { useSteeringLevelsSubscription$data } from "@/modules/admin/capacity/services/__generated__/useSteeringLevelsSubscription.graphql"
import { useSteeringLevelsSubscription } from "@/modules/admin/capacity/services/useSteeringLevelsSubscription"
import { SteeringLevelIcon } from "@/modules/productivity-cockpit/components/steering-level-icon"
import { PlanningChart } from "@/modules/productivity-cockpit/planning-chart"
import { ProductivityCockpitCapacity } from "@/modules/productivity-cockpit/productivity-cockpit-capacity"

const ProductivityCockpit = () => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [steeringLevels, setSteeringLevels] = useState<useSteeringLevelsSubscription$data["steeringLevelsChanged"]>([])
  const user = useUser()
  const { t } = useTranslation()

  const onSteeringLevelsChanged = useCallback(
    (res: useSteeringLevelsSubscription$data | null | undefined) => {
      if (!res) return
      setSteeringLevels(res.steeringLevelsChanged)
    },
    [setSteeringLevels],
  )

  useSteeringLevelsSubscription(onSteeringLevelsChanged)

  return (
    <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
      <DialogTrigger asChild>
        <div data-testid="cockpit-dialog-trigger" className="flex items-center justify-center self-stretch">
          <div className="hover:bg-surface-hovered flex cursor-pointer flex-col items-stretch justify-center gap-4 rounded-[96px] bg-transparent px-2 py-8 2xl:h-71 2xl:py-2">
            <Suspense fallback={null}>
              <TooltipCustom>
                <TooltipTrigger>
                  <div className="grid gap-1">
                    {steeringLevels.map((s) => (
                      <SteeringLevelIcon key={s.id} language={s.language} level={s.level} levelValue={s.levelValue} />
                    ))}
                  </div>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="right" align="center" className="py-2">
                    {t("common.menu.steeringLevel")}
                  </TooltipContent>
                </TooltipPortal>
              </TooltipCustom>
            </Suspense>
          </div>
        </div>
      </DialogTrigger>
      <DialogContentCustom variant="large" onPointerDownOutside={(e) => e.preventDefault()} className="max-w-max">
        <DialogHeader>
          <DialogTitle>{t("scheduling.productivityCockpit.title")}</DialogTitle>
        </DialogHeader>
        <div className="text-muted-foreground min-h-125 px-6 py-2 text-sm">
          {user.isOneOf(["MedicalSupervisor"]) ? (
            <Tabs defaultValue="planning">
              <TabsList variant="secondary">
                <TabsTrigger data-testid="planning-trigger" variant="secondary" value="planning" asChild>
                  <div>{t("scheduling.productivityCockpit.performance")}</div>
                </TabsTrigger>
                <TabsTrigger data-testid="capacity-trigger" variant="secondary" value="capacity" asChild>
                  <div>{t("scheduling.productivityCockpit.capacity")}</div>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="planning" className="m-0 h-160.75 w-375 p-0">
                <Suspense fallback={null}>
                  <PlanningChart />
                </Suspense>
              </TabsContent>
              <TabsContent value="capacity" className="m-0 h-160.75 w-375 p-0 pt-3">
                <ProductivityCockpitCapacity />
              </TabsContent>
            </Tabs>
          ) : (
            <ProductivityCockpitCapacity />
          )}
        </div>
        <DialogFooter>
          <Button data-testid="cockpit-close-btn" variant="secondary" onClick={() => setDialogOpen(false)}>
            {t("common.button.close")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { ProductivityCockpit }
